/**
 * One small icon set for the whole app, drawn as filled shapes: outlines
 * disappear on cheap phone screens and at 18px in a sidebar. Every icon sits
 * on a 24-unit grid and inherits `currentColor`, so it follows text colour
 * and both themes without any extra rules.
 */
const PATHS = {
  // Navigation
  home: 'M4 11 12 4l8 7v9a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1v-9Z',
  calendar:
    'M7 2v2H5.5A2.5 2.5 0 0 0 3 6.5v13A2.5 2.5 0 0 0 5.5 22h13a2.5 2.5 0 0 0 2.5-2.5v-13A2.5 2.5 0 0 0 18.5 4H17V2h-2v2H9V2H7Zm12 8v9.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V10h14Zm-9 2H7v3h3v-3Z',
  repeat: 'M17 2l4 4-4 4V7H8a3 3 0 0 0-3 3v2H3v-2a5 5 0 0 1 5-5h9V2ZM7 22l-4-4 4-4v3h9a3 3 0 0 0 3-3v-2h2v2a5 5 0 0 1-5 5H7v3Z',
  tree: 'M3 3h7v6H3V3Zm8 2h10v2H11V5ZM6 9v6h4v-2h7v2h4v-6h-4v2h-7V9H6Zm-3 8h7v4H3v-4Zm11 0h7v4h-7v-4Z',
  users: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-4 0-7 2-7 4.5V20h14v-2.5C16 15 13 13 9 13Zm8-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm.5 2.2c2.6.5 4.5 2 4.5 4V20h-4v-2.5c0-1.6-.6-3-1.7-4.1l1.2-.2Z',
  tools: 'M4 4h16v3H4V4Zm0 5.5h16v3H4v-3ZM4 15h10v3H4v-3Zm12.2 1.4 1.6 1.6 3.6-3.6 1.4 1.4-5 5-3-3 1.4-1.4Z',
  warning: 'M12 2 1.5 20.5h21L12 2Zm-1 6h2v6h-2V8Zm0 8h2v2h-2v-2Z',
  document: 'M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm7 1.5V9h5.5L13 3.5ZM8 12h8v2H8v-2Zm0 4h8v2H8v-2Z',
  handover: 'M4 4h9v2H6v12h7v2H4V4Zm10.5 3.5L20 12l-5.5 4.5V13H9v-2h5.5V7.5Z',
  // Actions
  plus: 'M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5Z',
  upload: 'M12 3l5 5-1.4 1.4L13 6.8V16h-2V6.8L8.4 9.4 7 8l5-5ZM4 15h2v4h12v-4h2v6H4v-6Z',
  download: 'M11 3h2v9.2l2.6-2.6L17 11l-5 5-5-5 1.4-1.4L11 12.2V3ZM4 15h2v4h12v-4h2v6H4v-6Z',
  search: 'M10 3a7 7 0 1 1-4.2 12.6l-3.4 3.4-1.4-1.4 3.4-3.4A7 7 0 0 1 10 3Zm0 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z',
  pin: 'M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z',
  edit: 'M15.5 3.5 20.5 8.5 8 21H3v-5L15.5 3.5Zm0 2.8L5 16.8V19h2.2L17.7 8.5l-2.2-2.2Z',
  archive: 'M3 3h18v5H3V3Zm1 7h16v11H4V10Zm5 2v2h6v-2H9Z',
  move: 'M12 2l4 4h-3v4h4V7l4 4-4 4v-3h-4v4h3l-4 4-4-4h3v-4H7v3l-4-4 4-4v3h4V6H8l4-4Z',
  print: 'M7 2h10v5H7V2ZM4 8h16a2 2 0 0 1 2 2v7h-4v5H6v-5H2v-7a2 2 0 0 1 2-2Zm4 8v4h8v-4H8Zm10-5a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z',
  route: 'M6 3a3 3 0 0 1 2.8 4H15a4 4 0 0 1 0 8h-6a2 2 0 0 0 0 4h6.2a3 3 0 1 1 0 2H9a4 4 0 0 1 0-8h6a2 2 0 0 0 0-4H8.8A3 3 0 1 1 6 3Z',
  check: 'M9.5 16.2 5.3 12l-1.4 1.4 5.6 5.6 12-12-1.4-1.4L9.5 16.2Z',
  send: 'M2 21 23 12 2 3v7l15 2-15 2v7Z',
  file: 'M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm7 1.5V9h5.5L13 3.5Z',
  table: 'M3 4h18v16H3V4Zm2 2v3h14V6H5Zm0 5v3h6v-3H5Zm8 0v3h6v-3h-6ZM5 16v2h6v-2H5Zm8 0v2h6v-2h-6Z',
  clock: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 5v5.3l3.6 2.1-.8 1.4L11 13V7h2Z',
  link: 'M10.6 13.4a1 1 0 0 1 0-1.4l3-3a1 1 0 0 1 1.4 1.4l-3 3a1 1 0 0 1-1.4 0ZM7 17a3 3 0 0 1 0-4.2l2.1-2.1 1.4 1.4L8.4 14.2a1 1 0 1 0 1.4 1.4l2.1-2.1 1.4 1.4L11.2 17A3 3 0 0 1 7 17Zm10-10a3 3 0 0 1 0 4.2l-2.1 2.1-1.4-1.4 2.1-2.1a1 1 0 1 0-1.4-1.4l-2.1 2.1-1.4-1.4L12.8 7A3 3 0 0 1 17 7Z',
  building: 'M4 21V3h10v6h6v12H4Zm2-2h2v-3H6v3Zm0-5h2v-3H6v3Zm0-5h2V6H6v3Zm4 10h2v-3h-2v3Zm0-5h2v-3h-2v3Zm0-5h2V6h-2v3Zm6 10h2v-3h-2v3Zm0-5h2v-3h-2v3Z',
  bolt: 'M13 2 4 14h6l-1 8 9-12h-6l1-8Z',
  close: 'M6.4 5 12 10.6 17.6 5 19 6.4 13.4 12l5.6 5.6-1.4 1.4L12 13.4 6.4 19 5 17.6 10.6 12 5 6.4 6.4 5Z',
  star: 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.5L12 17.3l-5.9 3.2 1.3-6.5L2.5 9.4l6.6-.8L12 2.5Z',
  gear: 'M13.9 2l.5 2.6c.6.2 1.2.5 1.7.9l2.5-.9 1.9 3.3-2 1.7c.1.6.1 1.2 0 1.8l2 1.7-1.9 3.3-2.5-.9c-.5.4-1.1.7-1.7.9l-.5 2.6h-3.8l-.5-2.6c-.6-.2-1.2-.5-1.7-.9l-2.5.9-1.9-3.3 2-1.7a6 6 0 0 1 0-1.8l-2-1.7 1.9-3.3 2.5.9c.5-.4 1.1-.7 1.7-.9L10.1 2h3.8ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  swap: 'M7 3 3 7l4 4V8h11V6H7V3Zm10 10v3H6v2h11v3l4-4-4-4Z',
  qr: 'M3 3h8v8H3V3Zm2 2v4h4V5H5Zm8-2h8v8h-8V3Zm2 2v4h4V5h-4ZM3 13h8v8H3v-8Zm2 2v4h4v-4H5Zm8-2h2v2h-2v-2Zm4 0h4v2h-2v2h-2v-4Zm-4 4h2v4h-2v-4Zm4 2h2v2h2v-4h2v6h-6v-4Z',
  lock: 'M7 10V7a5 5 0 0 1 10 0v3h2v12H5V10h2Zm2 0h6V7a3 3 0 0 0-6 0v3Zm3 4a1.5 1.5 0 0 0-1 2.6V18h2v-1.4a1.5 1.5 0 0 0-1-2.6Z',
  key: 'M14 3a7 7 0 0 1 0 14c-.7 0-1.3-.1-1.9-.3L10 19H8v2H6v2H2v-4l7.3-7.3A7 7 0 0 1 14 3Zm2 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z',
  sparkle: 'M11 2h2l1.2 5.8L20 9v2l-5.8 1.2L13 18h-2l-1.2-5.8L4 11V9l5.8-1.2L11 2Zm7 12h1.5l.5 2.5 2.5.5v1.5l-2.5.5-.5 2.5H18l-.5-2.5-2.5-.5V17l2.5-.5.5-2.5Z',
  logout: 'M10 4h9a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-9v-2h8V6h-8V4Zm-1.5 3.5L4 12l4.5 4.5 1.4-1.4L7.8 13H15v-2H7.8l2.1-2.1-1.4-1.4Z',
  chevronLeft: 'M15 5l-7 7 7 7 1.4-1.4L10.8 12l5.6-5.6L15 5Z',
  chevronRight: 'M9 5l7 7-7 7-1.4-1.4L13.2 12 7.6 6.4 9 5Z',
  chevronDown: 'M5 9l7 7 7-7-1.4-1.4L12 13.2 6.4 7.6 5 9Z',
  list: 'M4 5h2v2H4V5Zm4 0h12v2H8V5ZM4 11h2v2H4v-2Zm4 0h12v2H8v-2Zm-4 6h2v2H4v-2Zm4 0h12v2H8v-2Z',
  grid: 'M3 3h8v8H3V3Zm2 2v4h4V5H5Zm8-2h8v8h-8V3Zm2 2v4h4V5h-4ZM3 13h8v8H3v-8Zm2 2v4h4v-4H5Zm8-2h8v8h-8v-8Zm2 2v4h4v-4h-4Z',
} as const

export type IconName = keyof typeof PATHS

export function Icon({ name, size = 18, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      className={className ? `icon ${className}` : 'icon'}
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
